import type { CSSProperties, ReactElement } from 'react';

/* A state machine, rendered left to right, so a reader can see where a thing is and what is between
 * it and the next state.
 *
 * The design spec asks for this on Rules and describes **five** steps — draft, published, awaiting
 * approval, active, deprecated. The API has **three** rule states: `draft | active | deprecated`.
 * "Published" is a property of a *version*, not of a rule, and "awaiting approval" is the state of a
 * separate approval request. Rendering five would invent two states the platform does not have, and
 * a steward who then looked for "awaiting approval" in a filter would not find it.
 *
 * So this takes states and **gates** as different things, which is the more accurate reading and
 * says more than the five-step version would: a gate is not somewhere a rule waits, it is something
 * that must happen for the rule to move. The two gates on the rules lifecycle are publishing a
 * version and collecting two signatures, and neither is a state anybody can filter by.
 *
 * `reached` is what makes it a position rather than a diagram: a state the subject has not got to
 * yet is drawn hollow, matching every other hollow mark in this kit, because not-yet is an absence
 * and an absence must not render as a paler fill of the same shape.
 */

export interface LifecycleStep {
  /** The state's own label, from the copy catalogue. */
  readonly label: string;
  /** Whether the subject has reached this state. Hollow when false. */
  readonly reached: boolean;
  /** The dot's colour, as a token expression. */
  readonly colour?: string;
  /** What has to happen to leave this state, if anything. Rendered between this step and the next,
   * so it reads as a gate rather than as another state. */
  readonly gate?: string;
}

export interface LifecycleStripProps {
  readonly steps: readonly LifecycleStep[];
  /** One sentence of the rule the strip exists to make visible. */
  readonly note: string;
}

export function LifecycleStrip({ steps, note }: LifecycleStripProps): ReactElement {
  return (
    <div className="lq-lifecycle">
      {steps.map((step, index) => (
        /* Keyed on the label rather than the index: the labels are distinct states, so they are the
         * stable identity here, and an index key would reuse a DOM node across two different states
         * if the sequence ever changed. */
        <span key={step.label} className="lq-lifecycle__step">
          <span
            className={
              step.reached
                ? 'lq-lifecycle__dot'
                : 'lq-lifecycle__dot lq-lifecycle__dot--future'
            }
            style={
              step.colour === undefined
                ? undefined
                : ({ '--step-colour': step.colour } as CSSProperties)
            }
            aria-hidden="true"
          />
          {step.label}
          {step.gate !== undefined && (
            /* The gate, between two states. Set apart in parentheses because it is not a place a
             * rule can be — it is the condition on the arrow. */
            <span className="lq-lifecycle__arrow">{` → (${step.gate}) → `}</span>
          )}
          {step.gate === undefined && index < steps.length - 1 && (
            <span className="lq-lifecycle__arrow">{' → '}</span>
          )}
        </span>
      ))}
      <span className="lq-lifecycle__note">{note}</span>
    </div>
  );
}
